import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { StopButton } from "./Shell";
import BreathingPacer from "./BreathingPacer";
import ResponsivePacer from "./ResponsivePacer";
import AudioBreathControls from "./AudioBreathControls";
import AdaptivePacerBanner from "./AdaptivePacerBanner";
import Instructions from "./Instructions";
import useAmbientAudio from "../hooks/useAmbientAudio";
import useGazeTracker from "../hooks/useGazeTracker";
import useBreathAudioCue from "../hooks/useBreathAudioCue";
import { pickPatternForSUDS } from "../lib/adaptiveBreathing";

// Mode B — Hands-Free Bilateral Stimulation
// Smooth horizontal dot oscillation. Cadence 0.4–1.6 Hz. Adjustable duration.
// Optional webcam-based gaze tracking (opt-in, on-device only).

const DEFAULTS = { cadence: 0.8, duration: 180 };
const PACER_SIZES = { mobile: 200, desktop: 280 };

// Static "how to run Mode B" guidance — extracted so we don't allocate a
// fresh array on every render of the session screen.
const HOW_TO_RUN_MODE_B = [
  { strong: "Sit about an arm's length from the screen", text: "comfortable chair, both feet flat, spine relaxed." },
  { strong: "Soften your gaze", text: "let your eyes follow the dot without straining. It's tracking, not staring." },
  { strong: "Position your hands", text: "cross your arms over your chest for the butterfly hug, or rest both palms on your thighs." },
  { strong: "Breathe slowly through your nose", text: "if the breathing pacer is on, let your inhale + exhale match the pulsing circle." },
  { strong: "If you feel dizzy or queasy", text: "look away from the dot for a moment and slow the cadence down. Stop entirely if it persists." },
  { strong: "Optional · eye tracking", text: "you can enable webcam-based gaze tracking below. Video stays on this device — no frames are ever uploaded." },
];
// 5-point calibration targets as fractions of viewport (top-left, top-right,
// center, bottom-left, bottom-right) — converted to pixels at calibration time.
const CALIB_FRACS = [
  [0.1, 0.1],
  [0.9, 0.1],
  [0.5, 0.5],
  [0.1, 0.9],
  [0.9, 0.9],
];

export default function SessionModeB({ onComplete, onStop, preSuds = null }) {
  const adaptive = useMemo(
    () => (preSuds !== null && preSuds !== undefined ? pickPatternForSUDS(preSuds) : null),
    [preSuds]
  );

  const [running, setRunning] = useState(false);
  const [cadence, setCadence] = useState(DEFAULTS.cadence); // Hz (full L↔R cycles per second)
  const [duration, setDuration] = useState(DEFAULTS.duration); // seconds
  const [elapsed, setElapsed] = useState(0);
  const [breathing, setBreathing] = useState(true);
  // Pacer is always ON by default — matching your breath to the pulsing
  // light is the core vagal-tone mechanic. The user can still hide it.
  const [breathingPacerOn, setBreathingPacerOn] = useState(true);
  const [breathPattern, setBreathPattern] = useState(adaptive ? adaptive.pattern : "4-4-6-2");
  const audio = useAmbientAudio();
  const breathCue = useBreathAudioCue();

  // ---- gaze tracking ---------------------------------------------------
  const gaze = useGazeTracker();
  const [gazeOn, setGazeOn] = useState(false);          // user-facing toggle
  const [calibrating, setCalibrating] = useState(false); // overlay visible?
  const [calibIdx, setCalibIdx] = useState(0);
  const finalMetricsRef = useRef(null); // snapshot at finish so we don't lose them after stop()

  const startRef = useRef(null);
  const startedAtRef = useRef(null); // ISO wall-clock for wearable correlation
  const rafRef = useRef(null);
  const trackRef = useRef(null);
  const dotRef = useRef(null);

  // Animation loop
  useEffect(() => {
    if (!running) return;
    startRef.current = performance.now();
    const tick = (now) => {
      const sec = (now - startRef.current) / 1000;
      setElapsed(Math.min(sec, duration));
      if (trackRef.current && dotRef.current) {
        const w = trackRef.current.offsetWidth;
        const usable = w - 60; // dot ~36px + margin
        const phase = (Math.sin(2 * Math.PI * cadence * sec) + 1) / 2; // 0..1
        const x = 30 + phase * usable;
        dotRef.current.style.transform = `translate(${x - 18}px, -50%)`;
        // Feed gaze tracker with the dot's screen position (only when tracking is live)
        if (gaze.status === "tracking") {
          const rect = dotRef.current.getBoundingClientRect();
          gaze.tickAgainstDot(
            rect.left + rect.width / 2,
            rect.top + rect.height / 2,
            80 // hit radius in px — generous for clinical use
          );
        }
      }
      if (sec >= duration) {
        finish();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, cadence, duration, gaze.status]);

  const start = () => {
    setElapsed(0);
    startedAtRef.current = new Date().toISOString();
    setRunning(true);
  };

  // Build a metrics payload from current state. Used by both finish() (full
  // completion) and abort() (STOP — partial session).
  const buildMetrics = (isPartial) => {
    const wasTracking = gaze.status === "tracking";
    const snap = wasTracking ? { ...gaze.metrics } : null;
    const engagement = duration > 0 ? Math.min(1, elapsed / duration) : 0;
    return {
      mode: "B",
      cadence_hz: cadence,
      pacer_pattern: breathingPacerOn ? breathPattern : null,
      duration_seconds: Math.round(elapsed),
      engagement_score: Number(engagement.toFixed(3)),
      started_at: startedAtRef.current,
      ended_at: new Date().toISOString(),
      gaze_enabled: !!snap,
      gaze_calibrated: !!snap,
      gaze_on_dot_pct: snap ? snap.gazeOnDotPct : null,
      gaze_tracking_lag_ms: snap ? snap.trackingLagMs : null,
      gaze_stability_index: snap ? snap.stabilityIndex : null,
      gaze_dropout_count: snap ? snap.dropoutCount : null,
      gaze_sample_count: snap ? snap.sampleCount : null,
      partial: !!isPartial,
    };
  };

  const finish = () => {
    cancelAnimationFrame(rafRef.current);
    setRunning(false);
    audio.setEnabled(false);
    const metrics = buildMetrics(false);
    finalMetricsRef.current = metrics;
    if (gaze.status !== "idle") gaze.stop();
    onComplete(metrics);
  };

  // STOP — capture whatever was collected so far, mark partial, and let the
  // parent jump straight to the summary (skipping post-symptoms + grounding).
  const abort = () => {
    cancelAnimationFrame(rafRef.current);
    setRunning(false);
    audio.setEnabled(false);
    const metrics = buildMetrics(true);
    finalMetricsRef.current = metrics;
    if (gaze.status !== "idle") gaze.stop();
    onStop(metrics);
  };

  // ---- gaze toggle & calibration handlers ------------------------------
  const handleToggleGaze = useCallback(async () => {
    if (gazeOn) {
      // turn off & release camera
      setGazeOn(false);
      setCalibrating(false);
      setCalibIdx(0);
      gaze.stop();
      return;
    }
    setGazeOn(true);
    await gaze.start();
  }, [gazeOn, gaze]);

  // When camera is ready, automatically open the calibration overlay.
  useEffect(() => {
    if (gaze.status === "ready" && gazeOn && !calibrating) {
      const targets = CALIB_FRACS.map(([fx, fy]) => [
        Math.round(window.innerWidth * fx),
        Math.round(window.innerHeight * fy),
      ]);
      gaze.beginCalibration(targets);
      setCalibIdx(0);
      setCalibrating(true);
    }
    // gaze.beginCalibration is a stable useCallback — intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaze.status, gazeOn, calibrating]);

  const recordCurrentCalibPoint = useCallback(() => {
    // Sample after a short stare. We require the user to press SPACE / click
    // the target while looking at it.
    const ok = gaze.recordCalibrationPoint(calibIdx);
    if (!ok) return;
    if (calibIdx >= CALIB_FRACS.length - 1) {
      const fitted = gaze.finalizeCalibration();
      setCalibrating(false);
      if (!fitted) {
        // fit failed — turn camera off so the user falls back to no-tracking
        setGazeOn(false);
        gaze.stop();
      }
    } else {
      setCalibIdx((i) => i + 1);
    }
  }, [calibIdx, gaze]);

  const skipCalibration = useCallback(() => {
    // User opted out of calibration → revert to simple Mode B (no webcam).
    setCalibrating(false);
    setGazeOn(false);
    gaze.stop();
  }, [gaze]);

  // SPACE = record point during calibration
  useEffect(() => {
    if (!calibrating) return;
    const onKey = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        recordCurrentCalibPoint();
      } else if (e.code === "Escape") {
        skipCalibration();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [calibrating, recordCurrentCalibPoint, skipCalibration]);

  // Stop camera if user leaves Mode B. We capture gaze.stop into a ref once;
  // depending on `gaze` here would re-run cleanup on every render (gaze
  // returns a fresh object reference each render) — which would silently
  // shut the camera down right after it starts.
  const gazeStopRef = useRef(gaze.stop);
  useEffect(() => { gazeStopRef.current = gaze.stop; }, [gaze.stop]);
  useEffect(() => () => gazeStopRef.current && gazeStopRef.current(), []);

  const remaining = Math.max(0, duration - elapsed);
  const gazeLabel = gazeStatusLabel(gaze.status);

  return (
    <div className="bg-aurora min-h-screen relative overflow-hidden" data-testid="session-mode-b">
      <StopButton onClick={abort} />
      {breathing && <div className="breathe-halo" />}

      {/* Hidden video sink — required for MediaPipe to read frames. Never displayed. */}
      <video
        ref={gaze.videoRef}
        playsInline
        muted
        autoPlay
        className="absolute opacity-0 pointer-events-none"
        style={{ width: 1, height: 1, top: -9999, left: -9999 }}
        data-testid="mode-b-hidden-video"
      />

      <div className="relative z-10 max-w-7xl mx-auto pt-8 px-6 sm:px-10 flex items-center justify-between">
        <div className="font-mono text-xs tracking-[0.28em] uppercase text-[color:var(--accent-cyan)]">
          Mode B · Hands-Free
        </div>
        <div className="flex items-center gap-3">
          <GazeStatusPill status={gaze.status} label={gazeLabel} />
          <div className="font-mono text-xs tracking-[0.28em] uppercase text-[color:var(--text-secondary)] hidden sm:block">
            Soft gaze — follow the dot
          </div>
        </div>
      </div>

      {/* Instructions panel — shown only before the session starts */}
      {!running && (
        <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 mt-6">
          <Instructions title="How to run Mode B" items={HOW_TO_RUN_MODE_B} />
        </div>
      )}

      {/* Center HUD */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 md:px-10 mt-4 sm:mt-6">
        <div className="glass rounded-3xl px-4 sm:px-8 py-4 sm:py-6 scanlines relative">
          <div className="grid grid-cols-3 gap-3 sm:gap-6 items-end">
            <Metric label="Elapsed" value={formatTime(elapsed)} />
            <Metric label="Remaining" value={formatTime(remaining)} />
            <Metric label="Cadence" value={`${cadence.toFixed(2)} Hz`} accent />
          </div>
          <div className="mt-4 h-1 w-full bg-[color:var(--bg-soft)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[color:var(--accent-cyan)]"
              style={{ width: `${(elapsed / duration) * 100}%`, boxShadow: "0 0 12px var(--accent-cyan-glow)" }}
            />
          </div>
        </div>
      </div>

      {/* The Dot Track */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 mt-12">
        <div
          ref={trackRef}
          className="relative h-32 sm:h-40 rounded-3xl glass overflow-hidden"
          data-testid="mode-b-track"
        >
          {/* horizontal guide line */}
          <div
            className="absolute left-6 right-6 top-1/2 h-px"
            style={{
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(0,191,255,0.4) 50%, rgba(255,255,255,0) 100%)",
            }}
          />
          <div
            ref={dotRef}
            className="absolute top-1/2 bs-dot"
            style={{ transform: "translate(30px, -50%)" }}
            data-testid="mode-b-dot"
          />
        </div>
      </div>

      {/* Always-on breathing pacer — large, centered, with instruction banner */}
      {breathingPacerOn && (
        <div
          className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 mt-10 flex flex-col items-center"
          data-testid="mode-b-pacer-section"
        >
          <div
            className="font-mono text-[11px] tracking-[0.32em] uppercase mb-4 text-center"
            style={{ color: "var(--royal-blue, #153A79)" }}
          >
            Match your breath to the pulsing light
          </div>
          <ResponsivePacer
            active={breathingPacerOn}
            pattern={breathPattern}
            onPhaseChange={breathCue.notify}
            sizes={PACER_SIZES}
          />
        </div>
      )}

      {/* Controls */}
      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 mt-10">
        <div className="glass rounded-3xl p-6 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)]">
                Cadence (Hz)
              </label>
              <div className="font-mono text-cyan-glow">{cadence.toFixed(2)}</div>
            </div>
            <input
              type="range"
              min="0.4"
              max="1.6"
              step="0.05"
              value={cadence}
              onChange={(e) => setCadence(Number(e.target.value))}
              className="w-full accent-[color:var(--accent-cyan)]"
              data-testid="mode-b-cadence-slider"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)]">
                Duration (sec)
              </label>
              <div className="font-mono text-cyan-glow">{duration}s</div>
            </div>
            <input
              type="range"
              min="60"
              max="600"
              step="30"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full accent-[color:var(--accent-cyan)]"
              data-testid="mode-b-duration-slider"
              disabled={running}
            />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setBreathing((b) => !b)}
              data-testid="mode-b-breathing-toggle"
            >
              Halo: {breathing ? "ON" : "OFF"}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => breathCue.setEnabled(!breathCue.enabled)}
              data-testid="mode-b-breath-cue-toggle"
              title="Soft 'in… / out…' tones synced to the pulse"
            >
              Breath cues: {breathCue.enabled ? "ON" : "OFF"}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={handleToggleGaze}
              disabled={running || gaze.status === "loading"}
              data-testid="mode-b-eye-tracking-toggle"
              title="Webcam-based gaze tracking — opt-in, on-device only"
            >
              Eye tracking: {gazeOn ? "ON" : "OFF"}
            </button>
            {!running ? (
              <button
                type="button"
                className="btn-primary"
                onClick={start}
                disabled={calibrating || gaze.status === "loading"}
                data-testid="mode-b-start-btn"
              >
                Start ▶
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={finish}
                data-testid="mode-b-finish-btn"
              >
                Finish ✓
              </button>
            )}
          </div>
          {gaze.status === "denied" && (
            <div className="text-xs text-red-300 font-mono" data-testid="mode-b-gaze-denied">
              Camera permission denied. Re-enable it in your browser site settings to use eye tracking.
            </div>
          )}
          {gaze.status === "error" && gaze.error && (
            <div className="text-xs text-amber-300 font-mono" data-testid="mode-b-gaze-error">
              Eye-tracking error: {gaze.error}
            </div>
          )}
        </div>

        {adaptive && (
          <div className="mt-4">
            <AdaptivePacerBanner decision={adaptive} preSuds={preSuds} />
          </div>
        )}

        <div className="mt-4 flex justify-center">
          <AudioBreathControls
            audio={audio}
            breathingOn={breathingPacerOn}
            setBreathingOn={setBreathingPacerOn}
            pattern={breathPattern}
            setPattern={setBreathPattern}
          />
        </div>
      </div>

      <div className="fixed bottom-4 left-0 right-0 text-center z-10 px-4">
        <div className="inline-block glass rounded-full px-4 py-1.5 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)]">
          Tip · Soften your gaze. Let the dot move you.
        </div>
      </div>

      {/* 5-point calibration overlay */}
      {calibrating && (
        <CalibrationOverlay
          fracs={CALIB_FRACS}
          currentIdx={calibIdx}
          onRecord={recordCurrentCalibPoint}
          onSkip={skipCalibration}
        />
      )}
    </div>
  );
}

function CalibrationOverlay({ fracs, currentIdx, onRecord, onSkip }) {
  const target = fracs[currentIdx];
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center"
      data-testid="mode-b-calibration-overlay"
    >
      {/* current calibration target */}
      <button
        type="button"
        onClick={onRecord}
        aria-label={`Calibration point ${currentIdx + 1} of ${fracs.length}`}
        data-testid="mode-b-calibration-target"
        className="absolute rounded-full"
        style={{
          left: `${target[0] * 100}%`,
          top: `${target[1] * 100}%`,
          width: 28,
          height: 28,
          transform: "translate(-50%, -50%)",
          background: "var(--accent-cyan, #00bfff)",
          boxShadow: "0 0 30px 8px rgba(0,191,255,0.7)",
          border: "2px solid white",
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-md text-center px-6 pointer-events-none">
        {/* Hide the centered instructions when the target is also at center, to avoid covering it */}
        {(currentIdx !== 2) && (
          <>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-cyan)] mb-3">
              Calibration · {currentIdx + 1} / {fracs.length}
            </div>
            <h3 className="text-white text-2xl font-light mb-3">
              Look at the glowing dot
            </h3>
            <p className="text-white/70 text-sm font-mono leading-relaxed">
              Then press <kbd className="px-2 py-0.5 rounded bg-white/10 border border-white/20">SPACE</kbd> — or click the dot — to record this point.
            </p>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onSkip}
        data-testid="mode-b-calibration-skip"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full border border-white/30 text-white/80 text-xs font-mono tracking-[0.24em] uppercase hover:bg-white/10"
      >
        Skip calibration
      </button>
    </div>
  );
}

const GAZE_STATUS_COLOR = {
  tracking: "#22c55e",
  ready: "#f59e0b",
  calibrating: "#f59e0b",
  loading: "#38bdf8",
  error: "#ef4444",
  denied: "#ef4444",
};

function GazeStatusPill({ status, label }) {
  const dotColor = GAZE_STATUS_COLOR[status] || "rgba(255,255,255,0.35)";
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)]"
      data-testid="mode-b-gaze-status-pill"
    >
      <span
        className="inline-block rounded-full"
        style={{ width: 8, height: 8, background: dotColor, boxShadow: `0 0 8px ${dotColor}` }}
      />
      {label}
    </div>
  );
}

function gazeStatusLabel(status) {
  switch (status) {
    case "idle": return "Camera off";
    case "loading": return "Loading…";
    case "ready": return "Calibrating…";
    case "calibrating": return "Calibrating…";
    case "tracking": return "Tracking ✓";
    case "denied": return "Permission denied";
    case "error": return "Error";
    default: return "Camera off";
  }
}

function Metric({ label, value, accent = false }) {
  return (
    <div className="flex flex-col items-center sm:items-start">
      <div className="text-[10px] font-mono tracking-[0.24em] uppercase text-[color:var(--text-secondary)]">
        {label}
      </div>
      <div
        className={`font-mono text-2xl sm:text-3xl ${
          accent ? "text-cyan-glow" : "text-ink"
        } tracking-wider font-light`}
      >
        {value}
      </div>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
